import type { Candle, StrategyAnalysis } from '../types/market';
import type { SeriesMarker, UTCTimestamp } from 'lightweight-charts';

type WorkerResponse =
  | { type: 'RESULT'; id: string; analysis: StrategyAnalysis }
  | { type: 'PATTERNS_RESULT'; id: string; markers: SeriesMarker<UTCTimestamp>[] }
  | { type: 'ERROR'; id: string; error: string };

const ANALYSIS_TIMEOUT_MS = 10_000;

class StrategyClient {
  private readonly worker = new Worker(
    new URL('../workers/strategy.worker.ts', import.meta.url),
    { type: 'module' }
  );

  private readonly pending = new Map<
    string,
    {
      resolve: (value: any) => void;
      reject: (reason?: unknown) => void;
      timer: number;
    }
  >();

  constructor() {
    this.worker.addEventListener('message', (event: MessageEvent<WorkerResponse>) => {
      const pending = this.pending.get(event.data.id);
      if (!pending) return;
      window.clearTimeout(pending.timer);
      this.pending.delete(event.data.id);

      if (event.data.type === 'RESULT') pending.resolve(event.data.analysis);
      else if (event.data.type === 'PATTERNS_RESULT') {
        const rawMarkers = event.data.markers as SeriesMarker<UTCTimestamp>[];
        // Merge markers with the same timestamp
        const merged: SeriesMarker<UTCTimestamp>[] = [];
        for (const m of rawMarkers) {
          const last = merged[merged.length - 1];
          if (last && last.time === m.time && last.position === m.position) {
            if (!last.text?.includes(m.text || '')) {
              last.text += `, ${m.text}`;
            }
          } else {
            merged.push({ ...m });
          }
        }
        console.log(`[WASM] Found ${rawMarkers.length} raw markers, merged into ${merged.length}`);
        pending.resolve(merged);
      }
      else pending.reject(new Error(event.data.error));
    });

    this.worker.addEventListener('error', (event) => {
      const error = new Error(event.message || 'Rust/WASM strategy worker failed to load');
      this.rejectAll(error);
    });
  }

  analyze(candles: Candle[]): Promise<StrategyAnalysis> {
    const id = crypto.randomUUID();
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.pending.delete(id);
        reject(new Error('Rust/WASM strategy analysis timed out'));
      }, ANALYSIS_TIMEOUT_MS);

      const recentCandles = candles.length > 250 ? candles.slice(-250) : candles;
      this.pending.set(id, { resolve, reject, timer });
      this.worker.postMessage({ type: 'ANALYZE', id, candles: recentCandles });
    });
  }

  detectPatterns(candles: Candle[]): Promise<SeriesMarker<UTCTimestamp>[]> {
    const id = crypto.randomUUID();
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.pending.delete(id);
        reject(new Error('Rust/WASM pattern detection timed out'));
      }, ANALYSIS_TIMEOUT_MS);

      this.pending.set(id, { resolve, reject, timer });
      this.worker.postMessage({ type: 'DETECT_PATTERNS', id, candles });
    });
  }

  private rejectAll(error: Error) {
    for (const [id, pending] of this.pending) {
      window.clearTimeout(pending.timer);
      pending.reject(error);
      this.pending.delete(id);
    }
  }
}

export const strategyClient = new StrategyClient();
